import { create } from "zustand";
import { axiosInstance } from "../lib/axios";
import toast from "react-hot-toast";

export interface AdminOverview {
  totals: {
    revenue: number;
    projects: number;
    activeProjects: number;
    completedProjects: number;
    bids: number;
    jobsWon: number;
    connectsAllocated: number;
    connectsUsed: number;
    connectsRemaining: number;
    activeBDs: number;
    clients: number;
  };
  ai: {
    avgConnectsPerBid: number;
    avgProjectAmount: number;
    topConvertingCategory: string;
    avgProposalsPerMonth: number;
    bidEfficiency: number;
  };
}

export interface AdminProfile {
  accountId: string;
  name: string;
  rankingBadge: string | null;
  jobSuccessRate: number | null;
  totalEarnings: number;
  totalJobs: number;
  totalHours: number;
  conversionRate: number;
  connectsUsed: number;
  totalConnects: number;
  connectsUsedPct: number;
  totalBids: number;
  jobsWon: number;
  dataSource: "screenshot" | "scraped" | "manual";
  lastSnapshotAt: string | null;
  activeProjects: number;
}

export interface MilestoneItem {
  order: number;
  title: string;
  amount: number;
  status: "Pending" | "In Escrow" | "Released" | string;
  dueDate: string | null;
  releasedAt: string | null;
  notes: string;
}

export interface AdminProfileProject {
  _id: string;
  name: string;
  projectType: string;
  projectStatus: string;
  ownedBy: string;
  assignedTo: string;
  completionPct: number;
  milestonesReleased: number;
  milestonesTotal: number;
  milestoneItems: MilestoneItem[];
  total: number;
  escrow: number;
  released: number;
  origin: "BID" | "ORGANIC";
  priceModel: "fixed" | "hourly";
}

export interface ResourceValue {
  amount: number;
  type: "fixed" | "hourly" | "percentage";
  suffix: string;
}

export interface BdPerformanceRow {
  bdId: string;
  name: string;
  email: string;
  profilePic: string;
  totalRevenue: number;
  totalProjects: number;
  completedProjects: number;
  totalBids: number;
  jobsWon: number;
  winRate: number;
  totalConnects: number;
  proposalsCreated: number;
  questionnairesCreated: number;
  clientsManaged: number;
  perProfile: Record<
    string,
    { revenue: number; projects: number; bids: number; connects: number }
  >;
}

export interface FinancialsCategory {
  projectType: string;
  projects: number;
  revenue: number;
  laborCost: number;
  upworkFee: number;
  upworkFeeAmount: number;
  commission: number;
  commissionAmount: number;
  netProfit: number;
  margin: number;
}

export interface FinancialsProfile {
  accountId: string;
  name: string;
  projects: number;
  revenue: number;
  laborCost: number;
  upworkFee: number;
  commission: number;
  netProfit: number;
  connectsUsed: number;
  connectsAllocated: number;
}

export interface FinancialsBd {
  bdId: string;
  name: string;
  email: string;
  projects: number;
  revenue: number;
  netProfit: number;
}

export interface AdminFinancials {
  totals: {
    revenue: number;
    laborCost: number;
    upworkFee: number;
    commission: number;
    netProfit: number;
    margin: number;
    activeBDs: number;
    projects: number;
  };
  byCategory: FinancialsCategory[];
  byProfile: FinancialsProfile[];
  byBd: FinancialsBd[];
}

export interface CostCategory {
  projectType: string;
  fixedCost: { amount: number; type: string } | null;
  rangeCost: { min: number; max: number } | null;
  graphicDesigner: ResourceValue | null;
  coordinator: ResourceValue | null;
  otherFee: ResourceValue | null;
  defaultResources: { label: string; type: string; amount: number; hours: number }[];
  upworkFee: number;
  commission: number;
  projectsCount: number;
  revenue: number;
}

export interface RecentUpload {
  url: string;
  uploadedBy: string;
  uploadedAt: string;
  source: string;
  refId: string;
  platformAccount: string;
}

export interface AdminClientRow {
  _id: string;
  name: string;
  username: string;
  email: string;
  phone: string;
  company: string;
  website: string;
  leadStatus: string;
  contactStatus?: "Lead" | "Client";
  leadSource: string;
  notes: string;
  bd: { _id: string; name: string; email: string } | null;
  hasLogin: boolean;
  createdAt: string;
  projects: {
    total: number;
    active: number;
    completed: number;
    revenue: number;
  };
}

export interface AdminClientsResponse {
  clients: AdminClientRow[];
  totals: {
    total: number;
    won: number;
    lost: number;
    active: number;
    withLogin: number;
  };
  byStatus: { status: string; count: number }[];
  bySource: { source: string; count: number }[];
  byBd: {
    bdId: string;
    name: string;
    total: number;
    won: number;
    lost: number;
    active: number;
  }[];
}

export interface AdminProjectRow {
  _id: string;
  name: string;
  projectType: string;
  projectStatus: string;
  designType: string;
  priceModel: "fixed" | "hourly";
  total: number;
  escrow: number;
  released: number;
  paymentStatus: string;
  eta: string | null;
  bd: { _id: string; name: string; email: string } | null;
  client: { _id: string; name: string; email?: string } | null;
  profile: { _id: string; name: string } | null;
  source?: "Upwork" | "Direct" | "Invitation";
  sourceBid?: boolean;
  origin: "BID" | "ORGANIC";
  completionPct: number;
  attachments?: {
    _id: string;
    name: string;
    url: string;
    r2Key: string;
    mimeType: string;
    size: number;
    category: string;
  }[];
  links?: { proposal: boolean; upworkJob: boolean; wonBid: boolean };
  financials?: {
    netProfit: number;
    marginPct: number;
    targetMarginPct: number;
    profitStatus: "Profit" | "Loss" | "Below Target";
  };
  milestones: {
    total: number;
    released: number;
    inEscrow: number;
    items: {
      title: string;
      amount: number;
      status: string;
      dueDate: string | null;
      releasedAt: string | null;
    }[];
  };
  resourceCosts: { label: string; type: string; amount: number; hours: number }[];
  createdAt: string;
  updatedAt: string;
}

export interface AdminProjectsResponse {
  projects: AdminProjectRow[];
  totals: {
    total: number;
    active: number;
    completed: number;
    onHold: number;
    revenue: number;
    released: number;
    escrow: number;
  };
  byStatus: { status: string; count: number; revenue: number }[];
  byType: { type: string; count: number; revenue: number }[];
  byProfile: {
    profileId: string;
    name: string;
    count: number;
    revenue: number;
    completed: number;
  }[];
  byBd: {
    bdId: string;
    name: string;
    count: number;
    revenue: number;
    completed: number;
  }[];
}

export interface AccountPerformanceCard {
  accountId: string;
  accountName: string;
  metrics: {
    avgConnectsPerBid: number;
    avgProjectAmount: number;
    topConvertingCategory: string | null;
    topCategoryConversionPct: number;
    avgProposalsPerMonth: number;
    bidEfficiencyScore: number;
    totalBids: number;
    totalConnects: number;
    wonBids: number;
    winRate: number;
    completedProjects: number;
    totalRevenue: number;
  };
  breakdown: {
    scoreInputs: {
      winRateScore: number;
      connectEffScore: number;
      dealSizeScore: number;
      categoryStrengthScore: number;
    };
    categories: Record<string, { won: number; total: number }>;
  };
}

export interface AccountPerformanceResponse {
  period: string;
  since: string | null;
  until: string;
  accounts: AccountPerformanceCard[];
}

export type ResourceValueDoc = {
  amount: number;
  type: "fixed" | "hourly" | "percentage";
  hours: number;
};

export type ExtraRoleDoc = {
  label: string;
  amount: number;
  type: "fixed" | "hourly" | "percentage";
  hours: number;
};

export type AssignedResourceDoc = {
  erpId: string | null;
  name: string;
  email: string;
  role: string;
  hours: number;
  ratePerHourUSD: number;
};

export interface CostConfig {
  _id: string;
  projectType: string;
  projectSubType?: string;
  clientTier: "Basic" | "Standard" | "Premium";
  upworkAccount: { _id: string; name: string } | null;
  fixedCost: ResourceValueDoc;
  rangeCost: { min: number; max: number };
  graphicDesignerCost: ResourceValueDoc;
  coordinatorCost: ResourceValueDoc;
  commission: number;
  upworkFee: number;
  profitMarginPct?: number;
  otherFees: {
    label: string;
    amount: number;
    type: "fixed" | "hourly" | "percentage";
  };
  extraRoles: ExtraRoleDoc[];
  assignedResources?: AssignedResourceDoc[];
  notes: string;
  createdBy?: { _id: string; fullName: string; email: string } | string;
  createdAt: string;
  updatedAt: string;
}

export interface CostConfigsResponse {
  items: CostConfig[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

export interface BidReportRow {
  _id: string;
  platformAccount: string;
  bd: string;
  contact: string;
  bidAmount: number;
  connectsUsed: number;
  status: string;
  boostUsed: boolean;
  createdAt: string;
}

interface AdminState {
  loading: boolean;
  overview: AdminOverview | null;
  profiles: AdminProfile[];
  profileProjects: Record<string, AdminProfileProject[]>;
  bdPerformance: BdPerformanceRow[];
  costManagement: {
    globals: {
      upworkFee: number;
      commission: number;
      costPerConnect: number;
      avgConnectsPerWin: number;
    };
    categories: CostCategory[];
  } | null;
  recentUploads: RecentUpload[];
  bidsReport: BidReportRow[];
  financials: AdminFinancials | null;
  adminClients: AdminClientsResponse | null;
  adminProjects: AdminProjectsResponse | null;
  accountPerformance: AccountPerformanceResponse | null;
  costConfigs: CostConfigsResponse | null;

  fetchAll: () => Promise<void>;
  fetchProfileProjects: (profileId: string) => Promise<void>;
  fetchBidsReport: (params?: { platformAccount?: string; bdId?: string }) => Promise<void>;
  fetchFinancials: () => Promise<void>;
  fetchBdPerformance: () => Promise<void>;
  fetchAdminClients: (params?: {
    bdId?: string;
    status?: string;
    source?: string;
    search?: string;
  }) => Promise<void>;
  fetchAdminProjects: (params?: {
    bdId?: string;
    profileId?: string;
    clientId?: string;
    status?: string;
    type?: string;
    origin?: string;
    search?: string;
  }) => Promise<void>;

  fetchAccountPerformance: (params?: {
    period?: string;
    from?: string;
    to?: string;
  }) => Promise<void>;

  fetchCostConfigs: (params?: {
    projectType?: string;
    clientTier?: string;
    upworkAccount?: string;
    search?: string;
    page?: number;
    limit?: number;
    sortBy?: string;
    sortDir?: "asc" | "desc";
  }) => Promise<void>;

  createCostConfig: (body: Partial<CostConfig>) => Promise<CostConfig | null>;
  updateCostConfig: (
    id: string,
    body: Partial<CostConfig>
  ) => Promise<CostConfig | null>;
  deleteCostConfig: (id: string) => Promise<boolean>;
  duplicateCostConfig: (
    id: string,
    overrides?: Partial<CostConfig>
  ) => Promise<CostConfig | null>;
}

export const useAdminStore = create<AdminState>((set, get) => ({
  loading: false,
  overview: null,
  profiles: [],
  profileProjects: {},
  bdPerformance: [],
  costManagement: null,
  recentUploads: [],
  bidsReport: [],
  financials: null,
  adminClients: null,
  adminProjects: null,
  accountPerformance: null,
  costConfigs: null,

  fetchAll: async () => {
    set({ loading: true });
    try {
      const [overview, profiles, bd, cost, uploads] = await Promise.all([
        axiosInstance.get("/admin/overview"),
        axiosInstance.get("/admin/profiles"),
        axiosInstance.get("/admin/bd-performance"),
        axiosInstance.get("/admin/cost-management"),
        axiosInstance.get("/admin/recent-uploads"),
      ]);
      set({
        overview: overview.data,
        profiles: profiles.data.profiles,
        bdPerformance: bd.data.bds,
        costManagement: cost.data,
        recentUploads: uploads.data.uploads,
      });
    } catch (err: any) {
      const msg =
        err?.response?.data?.message || "Failed to load admin dashboard";
      toast.error(msg);
    } finally {
      set({ loading: false });
    }
  },

  fetchProfileProjects: async (profileId: string) => {
    try {
      const { data } = await axiosInstance.get(
        `/admin/profiles/${profileId}/projects`
      );
      set({
        profileProjects: {
          ...get().profileProjects,
          [profileId]: data.projects,
        },
      });
    } catch (err: any) {
      toast.error(
        err?.response?.data?.message || "Failed to load profile projects"
      );
    }
  },

  fetchBidsReport: async (params) => {
    try {
      const { data } = await axiosInstance.get("/admin/bids-report", {
        params,
      });
      set({ bidsReport: data.bids });
    } catch (err: any) {
      toast.error(
        err?.response?.data?.message || "Failed to load bids report"
      );
    }
  },

  fetchFinancials: async () => {
    try {
      const { data } = await axiosInstance.get("/admin/financials");
      set({ financials: data });
    } catch (err: any) {
      toast.error(
        err?.response?.data?.message || "Failed to load financials"
      );
    }
  },

  fetchBdPerformance: async () => {
    try {
      const { data } = await axiosInstance.get("/admin/bd-performance");
      set({ bdPerformance: data.bds });
    } catch (err: any) {
      toast.error(
        err?.response?.data?.message || "Failed to load BD performance"
      );
    }
  },

  fetchAdminClients: async (params) => {
    try {
      const { data } = await axiosInstance.get("/admin/clients", { params });
      set({ adminClients: data });
    } catch (err: any) {
      toast.error(
        err?.response?.data?.message || "Failed to load clients"
      );
    }
  },

  fetchAdminProjects: async (params) => {
    try {
      const { data } = await axiosInstance.get("/admin/projects", { params });
      set({ adminProjects: data });
    } catch (err: any) {
      toast.error(
        err?.response?.data?.message || "Failed to load projects"
      );
    }
  },

  fetchAccountPerformance: async (params) => {
    try {
      const { data } = await axiosInstance.get("/admin/account-performance", {
        params,
      });
      set({ accountPerformance: data });
    } catch (err: any) {
      toast.error(
        err?.response?.data?.message ||
          "Failed to load account performance"
      );
    }
  },

  fetchCostConfigs: async (params) => {
    try {
      const { data } = await axiosInstance.get("/admin/cost-configs", {
        params,
      });
      set({ costConfigs: data });
    } catch (err: any) {
      toast.error(
        err?.response?.data?.message || "Failed to load cost configs"
      );
    }
  },

  createCostConfig: async (body) => {
    try {
      const { data } = await axiosInstance.post("/admin/cost-configs", body);
      toast.success("Cost config created");
      await get().fetchCostConfigs();
      return data;
    } catch (err: any) {
      toast.error(
        err?.response?.data?.message || "Failed to create cost config"
      );
      return null;
    }
  },

  updateCostConfig: async (id, body) => {
    try {
      const { data } = await axiosInstance.put(
        `/admin/cost-configs/${id}`,
        body
      );
      toast.success("Cost config updated");
      await get().fetchCostConfigs();
      return data;
    } catch (err: any) {
      toast.error(
        err?.response?.data?.message || "Failed to update cost config"
      );
      return null;
    }
  },

  deleteCostConfig: async (id) => {
    try {
      await axiosInstance.delete(`/admin/cost-configs/${id}`);
      toast.success("Cost config deleted");
      await get().fetchCostConfigs();
      return true;
    } catch (err: any) {
      toast.error(
        err?.response?.data?.message || "Failed to delete cost config"
      );
      return false;
    }
  },

  duplicateCostConfig: async (id, overrides) => {
    try {
      const { data } = await axiosInstance.post(
        `/admin/cost-configs/${id}/duplicate`,
        overrides || {}
      );
      toast.success("Cost config duplicated");
      await get().fetchCostConfigs();
      return data;
    } catch (err: any) {
      toast.error(
        err?.response?.data?.message || "Failed to duplicate cost config"
      );
      return null;
    }
  },
}));
